// src/pages/admin/ContactManagement.jsx
import { useState, useEffect } from "react";
import { api } from "../../utils/api";
import toast from "react-hot-toast";

export default function ContactManagement() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState("all"); // all, unread, read

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    const res = await api.getAllContactMessages();
    if (res.success) {
      setMessages(res.data.messages || []);
    } else {
      toast.error("Failed to load messages");
    }
    setLoading(false);
  };

  const handleMarkAsRead = async (messageId) => {
    const res = await api.markContactMessageAsRead(messageId);
    if (res.success) {
      toast.success("Marked as read");
      fetchMessages();
    } else {
      toast.error("Failed to update message");
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;

    const res = await api.deleteContactMessage(messageId);
    if (res.success) {
      toast.success("Message deleted");
      setSelectedMessage(null);
      fetchMessages();
    } else {
      toast.error("Failed to delete message");
    }
  };

  const filteredMessages = messages.filter((msg) => {
    if (filter === "unread") return !msg.isRead;
    if (filter === "read") return msg.isRead;
    return true;
  });

  const unreadCount = messages.filter((m) => !m.isRead).length;

  const getSubjectLabel = (subject) => {
    const labels = {
      order: "Order Inquiry",
      product: "Product Question",
      shipping: "Shipping & Delivery",
      return: "Return & Refund",
      technical: "Technical Support",
      feedback: "Feedback",
      other: "Other",
    };
    return labels[subject] || "General";
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contact Messages</h2>
          <p className="text-gray-600 mt-1">
            {messages.length} total messages • {unreadCount} unread
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === "all"
                ? "bg-purple-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            All ({messages.length})
          </button>
          <button
            onClick={() => setFilter("unread")}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === "unread"
                ? "bg-purple-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setFilter("read")}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === "read"
                ? "bg-purple-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Read ({messages.length - unreadCount})
          </button>
        </div>
      </div>

      {filteredMessages.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-6xl mb-4">📭</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No messages found
          </h3>
          <p className="text-gray-600">
            {filter === "unread"
              ? "All caught up! No unread messages."
              : "Contact messages will appear here."}
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Messages List */}
          <div className="lg:col-span-1 space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
            {filteredMessages.map((message) => (
              <div
                key={message._id}
                onClick={() => {
                  setSelectedMessage(message);
                  if (!message.isRead) {
                    handleMarkAsRead(message._id);
                  }
                }}
                className={`bg-white rounded-lg shadow p-4 cursor-pointer transition hover:shadow-lg ${
                  selectedMessage?._id === message._id
                    ? "ring-2 ring-purple-600"
                    : ""
                } ${!message.isRead ? "border-l-4 border-purple-600" : ""}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {message.name}
                  </h3>
                  {!message.isRead && (
                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">{message.email}</p>
                <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full mb-2">
                  {getSubjectLabel(message.subject)}
                </span>
                {message.orderNumber && (
                  <p className="text-xs text-gray-500 mb-2">
                    Order: {message.orderNumber}
                  </p>
                )}
                <p className="text-sm text-gray-700 line-clamp-2">
                  {message.message}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {new Date(message.createdAt).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            ))}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2">
            {selectedMessage ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-6 pb-6 border-b">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {selectedMessage.name}
                      </h2>
                      {!selectedMessage.isRead && (
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full font-semibold">
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600">{selectedMessage.email}</p>
                    {selectedMessage.phone && (
                      <p className="text-gray-600">📞 {selectedMessage.phone}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(selectedMessage._id)}
                    className="text-red-600 hover:text-red-700 font-medium"
                  >
                    🗑️ Delete
                  </button>
                </div>

                {/* Details */}
                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Subject</p>
                    <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                      {getSubjectLabel(selectedMessage.subject)}
                    </span>
                  </div>

                  {selectedMessage.orderNumber && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Order Number</p>
                      <p className="font-semibold text-gray-900">
                        {selectedMessage.orderNumber}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-gray-600 mb-1">Received</p>
                    <p className="text-gray-900">
                      {new Date(selectedMessage.createdAt).toLocaleString("en-IN", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>

                {/* Message Content */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Message:</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {selectedMessage.message}
                  </p>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-3">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${getSubjectLabel(
                      selectedMessage.subject
                    )}`}
                    className="flex-1 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition text-center font-semibold"
                  >
                    📧 Reply via Email
                  </a>
                  {selectedMessage.phone && (
                    <a
                      href={`tel:${selectedMessage.phone}`}
                      className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition text-center font-semibold"
                    >
                      📞 Call
                    </a>
                  )}
                  {selectedMessage.phone && (
                    <a
                      href={`https://wa.me/${selectedMessage.phone.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition text-center font-semibold"
                    >
                      💬 WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-6xl mb-4">📬</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Select a message
                </h3>
                <p className="text-gray-600">
                  Choose a message from the list to view details
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}